import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import CatalogRequestModal from "@/components/CatalogRequestModal";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_import-hub-80/artifacts/r714uk97_BotBSlogo%20RGB.png";

const links = [
  { to: "/sale/legacy",      label: "Legacy Sale" },
  { to: "/sale/annual",      label: "Annual Sale" },
  { to: "/consignors",       label: "Consignors" },
  { to: "/notable-bulls",    label: "The Bulls" },
  { to: "/donor-cows",       label: "The Females" },
  { to: "/heterosis",        label: "Heterosis" },
  { to: "/accreditation",    label: "Accreditation" },
  { to: "/buyer-info",       label: "Buyer Info" },
];

export default function SiteHeader() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [catalogOpen, setCatalogOpen] = useState(false);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 inset-x-0 z-50 transition-all duration-300"
      style={{
        background: "#E9E3D2",
        borderBottom: "1px solid rgba(30,77,117,0.18)",
        boxShadow: scrolled ? "0 4px 18px rgba(14, 44, 70, 0.06)" : "none",
      }}
    >
      <div className="max-w-[1400px] mx-auto flex items-center justify-between px-5 md:px-10 h-16 md:h-[78px]">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-3 group" onClick={() => setOpen(false)}>
          <img
            src={LOGO_URL}
            alt="Bulls of the Big Sky logo"
            className="block w-12 h-12 md:w-14 md:h-14 object-contain"
            style={{ filter: "drop-shadow(0 1px 1px rgba(14,44,70,0.15))" }}
          />
          <span className="hidden sm:block">
            <div
              className="font-display text-base md:text-xl leading-none whitespace-nowrap"
              style={{ color: "var(--brass-deep)", letterSpacing: "0.04em" }}
            >
              Bulls of the Big Sky
            </div>
            <div
              className="font-display text-[9px] md:text-[11px] mt-1 whitespace-nowrap"
              style={{ color: "var(--brass)", letterSpacing: "0.32em", fontWeight: 600 }}
            >
              Simmental ★ Angus ★ SimAngus
            </div>
          </span>
        </Link>

        <nav className="hidden xl:flex items-center gap-6">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `eyebrow-muted text-[11px] hover:text-[color:var(--brass)] transition-colors whitespace-nowrap ${
                  isActive ? "text-[color:var(--brass)]" : ""
                }`
              }
              style={({ isActive }) => isActive ? { color: "var(--brass)" } : { color: "var(--text-primary)" }}
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-3 md:gap-4">
          <button
            type="button"
            data-testid="header-request-catalog"
            onClick={() => { setOpen(false); setCatalogOpen(true); }}
            className="px-3 md:px-5 py-2 md:py-2.5 text-[11px] md:text-xs font-bold uppercase whitespace-nowrap transition-colors hover:brightness-105"
            style={{
              fontFamily: "'Big Shoulders Display', sans-serif",
              letterSpacing: "0.14em",
              background: "var(--hay)",
              color: "#0E2C46",
              border: "2px solid var(--hay)",
            }}
          >
            <span className="hidden md:inline">Request&nbsp;</span>Catalog
          </button>
          <button
            data-testid="nav-menu-toggle"
            aria-label={open ? "Close menu" : "Open menu"}
            onClick={() => setOpen(!open)}
            className="xl:hidden w-10 h-10 flex flex-col items-center justify-center gap-1.5"
          >
            <span
              className="block w-6 h-[2px] transition-transform"
              style={{
                background: "var(--brass-deep)",
                transform: open ? "translateY(6px) rotate(45deg)" : "none",
              }}
            />
            <span
              className="block w-6 h-[2px] transition-opacity"
              style={{ background: "var(--brass-deep)", opacity: open ? 0 : 1 }}
            />
            <span
              className="block w-6 h-[2px] transition-transform"
              style={{
                background: "var(--brass-deep)",
                transform: open ? "translateY(-6px) rotate(-45deg)" : "none",
              }}
            />
          </button>
        </div>
      </div>

      {/* Mobile / tablet drawer */}
      {open && (
        <div
          data-testid="nav-mobile-panel"
          className="xl:hidden border-t"
          style={{
            background: "#E9E3D2",
            borderColor: "rgba(30,77,117,0.18)",
            maxHeight: "calc(100vh - 4rem)",
            overflowY: "auto",
          }}
        >
          <nav className="max-w-[1400px] mx-auto px-6 md:px-12 py-6 flex flex-col">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `font-display text-2xl md:text-3xl py-3 border-b transition-colors ${
                    isActive ? "text-[color:var(--brass)]" : ""
                  }`
                }
                style={{ borderColor: "rgba(30,77,117,0.1)", color: "var(--brass-deep)" }}
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/admin/login"
              onClick={() => setOpen(false)}
              className="eyebrow-muted text-[10px] mt-6 self-start"
            >
              Admin →
            </Link>
          </nav>
        </div>
      )}
      <CatalogRequestModal open={catalogOpen} onClose={() => setCatalogOpen(false)} />
    </header>
  );
}
